import { format, parseISO } from 'date-fns';
import { Bot, ChevronDown, ChevronUp, ExternalLink, MessageSquare, User, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { IndexPath } from '@/Config/app/router/index.path';
import { ConversationExampleDto } from '@/Domain/api/Analytics/dto/KBSources.dto';
import { Avatar, AvatarFallback } from '@/Domain/components/Shared/Avatar/Avatar';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/Domain/components/Shared/Empty/Empty';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  articleTitle: string;
  examples: ConversationExampleDto[];
  isLoading: boolean;
  isError: boolean;
};

const ExampleCard = ({ example }: { example: ConversationExampleDto }) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'kb_sources.examples' });
  const { scenario } = useParams<{ scenario: string }>();
  const [isSnippetExpanded, setIsSnippetExpanded] = useState(false);

  const formattedDate = (() => {
    try {
      return format(parseISO(example.message_date), 'MMM d, yyyy');
    } catch {
      return example.message_date;
    }
  })();

  return (
    <div className="border-b border-border last:border-b-0 pb-6 mb-6 last:mb-0 last:pb-0">
      {/* Date + View in Inbox row */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-muted-foreground">{formattedDate}</span>
        <Link
          to={`/i/${scenario}/${IndexPath.inbox.root}/${example.conversation_id}`}
          className="inline-flex items-center gap-1 rounded-sm text-xs text-info underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          {t('view_in_inbox')}
          <ExternalLink className="size-3" />
        </Link>
      </div>

      <div className="space-y-3">
        {/* Visitor message - left aligned */}
        {example.visitor_message_text && (
          <div className="flex justify-start">
            <div className="max-w-[85%]">
              <div className="flex items-center gap-1.5 mb-1">
                <Avatar className="h-5 w-5">
                  <AvatarFallback className="bg-muted">
                    <User className="size-3 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-muted-foreground font-medium">
                  {t('visitor_label')}
                </span>
              </div>
              {/* Text node, never innerHTML: visitor-authored content is returned unformatted
                  so React escapes it and no markup path exists. */}
              <div className="bg-card border border-border rounded-xl p-3.5 text-foreground break-words whitespace-pre-wrap">
                {example.visitor_message_text}
              </div>
            </div>
          </div>
        )}

        {/* AI message - right aligned */}
        <div className="flex justify-end">
          <div className="max-w-[85%]">
            <div className="flex items-center gap-1.5 mb-1 justify-end">
              <span className="text-sm text-muted-foreground font-medium">{t('ai_label')}</span>
              <Avatar className="h-5 w-5">
                <AvatarFallback className="bg-muted">
                  <Bot className="size-3 text-muted-foreground" />
                </AvatarFallback>
              </Avatar>
            </div>
            <div
              className="bg-muted rounded-xl p-3.5 text-foreground break-words [&_img]:max-w-full [&_img]:h-auto [&_a]:font-medium [&_a]:text-info [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: example.ai_message_text }}
            />
          </div>
        </div>

        {/* Source snippet - expandable */}
        {example.source_snippet && (
          <button
            onClick={() => setIsSnippetExpanded(!isSnippetExpanded)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer inline-flex items-center gap-1"
          >
            {isSnippetExpanded ? t('hide_source') : t('show_source')}
            {isSnippetExpanded ? (
              <ChevronUp className="size-3" />
            ) : (
              <ChevronDown className="size-3" />
            )}
          </button>
        )}
        {isSnippetExpanded && example.source_snippet && (
          <div className="bg-muted border border-border rounded-md px-3 py-2">
            <p className="text-xs text-muted-foreground italic whitespace-pre-wrap break-words">
              {example.source_snippet}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="space-y-6">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="space-y-3">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-16 w-3/4 rounded-xl" />
        <Skeleton className="h-20 w-3/4 rounded-xl ml-auto" />
      </div>
    ))}
  </div>
);

export const ConversationExamplesSheet = ({
  isOpen,
  onClose,
  articleTitle,
  examples,
  isLoading,
  isError,
}: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'kb_sources.examples' });

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        className="sm:max-w-xl overflow-y-auto"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onClose();
        }}
      >
        <SheetHeader className="flex-row items-start">
          <div className="min-w-0 flex-1">
            <SheetTitle>{t('title')}</SheetTitle>
            <SheetDescription className="line-clamp-2">{articleTitle}</SheetDescription>
          </div>
          <IconButton
            variant="secondary"
            className="shrink-0"
            aria-label={t('close')}
            onClick={onClose}
          >
            <X className="size-4" />
          </IconButton>
        </SheetHeader>

        <p className="text-sm text-muted-foreground mt-4 mb-4">{t('description')}</p>

        <div>
          {isLoading ? (
            <LoadingSkeleton />
          ) : isError ? (
            <div className="flex items-center justify-center py-12 text-destructive">
              {t('error_loading')}
            </div>
          ) : examples.length === 0 ? (
            <Empty className="py-12">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <MessageSquare className="size-5" />
                </EmptyMedia>
                <EmptyTitle>{t('no_examples')}</EmptyTitle>
                <EmptyDescription>{t('no_examples_description')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            examples.map((example) => (
              <ExampleCard key={example.conversation_id} example={example} />
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
